import type { CSSProperties } from 'react';

export type ProveedorEmpresaConfigApi = {
  empresa?: Record<string, unknown>;
  proveedor?: { id?: number; nombre?: string };
  alias?: string | null;
  alta?: string | null;
  contactos?: unknown[];
  contadores?: unknown[];
};

export function parseProveedorEmpresaConfig(raw: unknown): ProveedorEmpresaConfigApi | null {
  if (raw == null || typeof raw !== 'object') return null;
  let body = raw as Record<string, unknown>;
  if (body.data != null && typeof body.data === 'object' && !Array.isArray(body.data)) {
    body = body.data as Record<string, unknown>;
  }
  if ('empresa' in body || 'proveedor' in body || 'alta' in body || 'alias' in body) {
    return body as ProveedorEmpresaConfigApi;
  }
  return null;
}

export function proveedorEmpresaConfigAlta(config: ProveedorEmpresaConfigApi | null): string {
  if (config == null) return '';
  const v = config.alta;
  if (v == null || String(v).trim() === '') return '';
  return String(v).trim();
}

export function proveedorEmpresaConfigAlias(config: ProveedorEmpresaConfigApi | null): string {
  if (config == null) return '';
  const v = config.alias;
  if (v == null || String(v).trim() === '') return '';
  return String(v).trim();
}

/** 1 = Primario, 2 = Secundario (API proveedor config contactos). */
export type ProveedorContactoCorreo = 1 | 2;

export const CORREO_CONTACTO_PRIMARIO = 1 as const;
export const CORREO_CONTACTO_SECUNDARIO = 2 as const;

export function parseProveedorContactoCorreo(
  contacto: Record<string, unknown>,
  correoApi: unknown,
): ProveedorContactoCorreo {
  if (correoApi === 2 || correoApi === '2') return CORREO_CONTACTO_SECUNDARIO;
  if (correoApi === 1 || correoApi === '1') return CORREO_CONTACTO_PRIMARIO;
  const raw = String(correoApi ?? '').trim();
  if (raw.toUpperCase() === 'SECUNDARIO') return CORREO_CONTACTO_SECUNDARIO;
  if (raw.toUpperCase() === 'PRIMARIO') return CORREO_CONTACTO_PRIMARIO;
  const email = raw.toLowerCase();
  if (email) {
    const sec = String(contacto.correoElectronicoSecundario ?? '').trim().toLowerCase();
    if (sec && email === sec) return CORREO_CONTACTO_SECUNDARIO;
  }
  return CORREO_CONTACTO_PRIMARIO;
}

export function proveedorContactoCorreoToApi(correo: ProveedorContactoCorreo): number {
  return correo === CORREO_CONTACTO_SECUNDARIO ? 2 : 1;
}

export function buildProveedorEmpresaConfigPayload(args: {
  empresaId: number;
  proveedorId: number;
  alias?: string;
  alta?: string;
  contactos: {
    contacto: Record<string, unknown>;
    correo: ProveedorContactoCorreo;
    repDocs: 'SI' | 'NO';
    recordatorio: 'SI' | 'NO';
  }[];
  contadores: { usuario: Record<string, unknown> }[];
}): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    empresa: { id: args.empresaId },
    proveedor: { id: args.proveedorId },
  };
  const alias = args.alias?.trim();
  if (alias) payload.alias = alias;
  const alta = args.alta?.trim();
  if (alta) payload.alta = alta;

  payload.contactos = args.contactos
    .filter(c => c.contacto?.id != null)
    .map(c => ({
      contacto: { id: c.contacto.id },
      correo: proveedorContactoCorreoToApi(c.correo),
      repDocs: c.repDocs === 'SI',
      recordatorio: c.recordatorio === 'SI',
    }));

  payload.contadores = args.contadores
    .filter(c => c.usuario?.id != null)
    .map(c => ({ usuario: { id: c.usuario.id } }));

  return payload;
}

/** Convierte fecha API a valor `YYYY-MM-DD` para `<input type="date">`. */
export function toDateInputValue(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') {
    const head = v.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  }
  const d = new Date(String(v));
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const labelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 13,
  border: '1px solid var(--crm-border)',
  borderRadius: 7,
  background: 'var(--crm-input-bg)',
  color: 'var(--crm-text-primary)',
};

export function ProveedorConfigFields({
  loading = false,
  active = false,
  config = null,
  altaValue,
  onAltaChange,
  idPrefix = '',
}: {
  loading?: boolean;
  active?: boolean;
  config?: ProveedorEmpresaConfigApi | null;
  altaValue: string;
  onAltaChange?: (value: string) => void;
  idPrefix?: string;
}) {
  const proveedorNombre =
    active && config?.proveedor?.nombre?.trim() ? config.proveedor.nombre.trim() : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {active && loading ? (
        <p className="m-0 text-muted" style={{ fontSize: 12 }}>
          Cargando configuración de la compañía…
        </p>
      ) : null}
      {proveedorNombre ? (
        <p className="m-0" style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
          {proveedorNombre}
        </p>
      ) : null}
      <div>
        <label style={labelStyle} htmlFor={`${idPrefix}proveedor-config-alta`}>
          Alta
        </label>
        <input
          id={`${idPrefix}proveedor-config-alta`}
          type="date"
          value={altaValue}
          onChange={e => onAltaChange?.(e.target.value)}
          readOnly={onAltaChange == null}
          disabled={loading}
          style={{
            ...inputStyle,
            background: onAltaChange == null ? 'var(--crm-surface-muted)' : undefined,
          }}
        />
      </div>
    </div>
  );
}
